import React from 'react';
import { Table, Button, Badge, Dropdown, Spinner } from 'react-bootstrap';
import { formatDateTime } from '../../../../utils/dateUtils';
import { CampaignStatus } from '../../../../infrastructure/CampaignClient';
import type { CampaignListProps } from './types';
import { getCampaignStatusColor, getCampaignStatusText } from './types';

// Helper function to format date/time with UTC label
const formatDateTimeUTC = (date: Date): string => {
  const formatted = formatDateTime(date.toISOString());
  return `${formatted} (UTC)`;
};

const CampaignList: React.FC<CampaignListProps> = ({
  campaigns,
  onView,
  onEdit,
  onDelete,
  onStatusChange,
  loading = false,
}) => {
  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" />
        <div className="mt-2">Loading campaigns...</div>
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">No campaigns found</p>
      </div>
    );
  }

  const getStatusActions = (campaign: any) => {
    const actions = [];

    if (campaign.status === CampaignStatus.DRAFT) {
      actions.push(
        <Dropdown.Item key="publish" onClick={() => onStatusChange(campaign, CampaignStatus.PUBLISHED)}>
          Publish
        </Dropdown.Item>,
      );
    }

    if (campaign.status === CampaignStatus.PUBLISHED) {
      actions.push(
        <Dropdown.Item key="draft" onClick={() => onStatusChange(campaign, CampaignStatus.DRAFT)}>
          Move to Draft
        </Dropdown.Item>,
      );
    }

    return actions;
  };

  return (
    <div className="w-100">
      <Table responsive bordered hover className="align-middle w-100">
        <thead className="bg-light">
          <tr>
            <th style={{ width: '80px' }}>ID</th>
            <th style={{ width: '200px' }}>Name</th>
            <th style={{ width: '180px' }}>Start Date</th>
            <th style={{ width: '180px' }}>End Date</th>
            <th style={{ width: '120px' }}>Status</th>
            <th style={{ width: '150px' }}>Created</th>
            <th style={{ width: '300px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((campaign) => (
            <tr key={campaign.id}>
              <td>
                <span className="badge bg-primary">{campaign.id}</span>
              </td>
              <td>
                <strong>{campaign.name}</strong>
              </td>
              <td className="text-nowrap">{formatDateTimeUTC(campaign.startAt)}</td>
              <td className="text-nowrap">{formatDateTimeUTC(campaign.endAt)}</td>
              <td>
                <Badge bg={getCampaignStatusColor(campaign.status)}>{getCampaignStatusText(campaign.status)}</Badge>
              </td>
              <td className="text-nowrap">{campaign.createdAt ? formatDateTimeUTC(campaign.createdAt) : '-'}</td>
              <td>
                <div className="d-flex gap-2 flex-wrap">
                  <Button variant="outline-info" size="sm" onClick={() => onView(campaign)}>
                    View
                  </Button>
                  <Button variant="outline-primary" size="sm" onClick={() => onEdit(campaign)}>
                    Edit
                  </Button>
                  <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" size="sm">
                      Status
                    </Dropdown.Toggle>
                    <Dropdown.Menu>{getStatusActions(campaign)}</Dropdown.Menu>
                  </Dropdown>
                  <Button variant="outline-danger" size="sm" onClick={() => onDelete(campaign)}>
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default CampaignList;
